import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import LeadDrawer from "./LeadDrawer";
import CrmUpgrade from "./CrmUpgrade";
import {
  crmFetch, STAGES, formatMoney, formatDate, dueLabel, LEAD_SOURCES,
} from "./crmApi";
import "./Crm.css";

const EMPTY_FORM = {
  client_name: "", client_phone: "", client_email: "", client_city: "",
  event_type: "", event_date: "", venue: "", budget: "", quoted_amount: "",
  source: "", notes: "", next_follow_up: "",
};

/**
 * The pipeline board: one column per stage, drag a card (or use the drawer's
 * stage chips) to move a lead. ?new=1 opens the add-lead modal — the
 * dashboard's "New Lead" button lands here with that set.
 */
function CrmLeads() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(searchParams.get("new") === "1");
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [planBlocked, setPlanBlocked] = useState(false);

  const stageFilter = searchParams.get("stage");

  const load = useCallback(async () => {
    try {
      const d = await crmFetch("/api/crm/leads");
      setLeads(d.leads || []);
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // ?lead=<id> opens that lead's drawer — how Quotes and Calendar link here.
  // Runs once per id so closing the drawer doesn't immediately reopen it.
  const deepLinkId = searchParams.get("lead");
  useEffect(() => {
    if (!deepLinkId || leads.length === 0) return;
    const match = leads.find((l) => String(l.id) === String(deepLinkId));
    if (match) setOpenLeadId(match.id);
    searchParams.delete("lead");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId, leads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.client_name, l.client_phone, l.event_type, l.venue]
        .some((v) => v && String(v).toLowerCase().includes(q))
    );
  }, [leads, search]);

  const byStage = useMemo(() => {
    const map = {};
    for (const s of STAGES) map[s.key] = [];
    for (const l of filtered) (map[l.stage] || map.new).push(l);
    return map;
  }, [filtered]);

  const openLead = leads.find((l) => l.id === openLeadId) || null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const closeModal = () => {
    setShowModal(false);
    setFormError("");
    if (searchParams.get("new")) {
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const createLead = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) {
      setFormError("Client name is required.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await crmFetch("/api/crm/leads", { method: "POST", body: form });
      setForm(EMPTY_FORM);
      closeModal();
      load();
    } catch (err) {
      setFormError(err.message);
    }
    setSaving(false);
  };

  const moveLead = async (leadId, stage) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.stage === stage) return;
    // Optimistic move; reload settles the truth (and lost_reason stays for the drawer).
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage } : l)));
    try {
      await crmFetch(`/api/crm/leads/${leadId}/stage`, { method: "PATCH", body: { stage } });
    } catch { /* revert on failure */ }
    load();
  };

  const visibleStages = stageFilter
    ? STAGES.filter((s) => s.key === stageFilter)
    : STAGES;

  return (
    <div className="crm-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="crm-main">
        <header className="crm-header">
          <div className="crm-header-left">
            <button className="crm-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <div>
              <h2>🎯 Leads</h2>
              <p className="crm-header-sub">Drag a card to move it through your pipeline</p>
            </div>
          </div>
          <button className="crm-primary-btn" onClick={() => setShowModal(true)}>＋ New Lead</button>
        </header>

        <div className="crm-content">
          {planBlocked ? <CrmUpgrade /> : (
          <>
          <div className="crm-board-tools">
            <input
              className="crm-search"
              placeholder="🔍 Search by name, phone, event or venue…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {stageFilter && (
              <button
                className="crm-btn ghost"
                onClick={() => { searchParams.delete("stage"); setSearchParams(searchParams); }}
              >
                Showing "{STAGES.find((s) => s.key === stageFilter)?.label}" — show all
              </button>
            )}
          </div>

          {loading ? (
            <div className="crm-loading">Loading leads…</div>
          ) : leads.length === 0 ? (
            <div className="crm-empty">
              <span className="crm-empty-icon">🌱</span>
              No leads yet.
              <div className="crm-empty-hint">
                Every enquiry — a call, a DM, a wedding-fair chat — belongs here.
                Click "New Lead" to add your first.
              </div>
            </div>
          ) : (
            <div className="crm-board" style={stageFilter ? { gridTemplateColumns: "minmax(280px, 480px)" } : {}}>
              {visibleStages.map((s) => {
                const cards = byStage[s.key] || [];
                const quoted = cards.reduce((sum, l) => sum + (Number(l.quoted_amount) || 0), 0);
                return (
                  <div
                    key={s.key}
                    className={`crm-col ${dragOver === s.key ? "drag-over" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(s.key); }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(null);
                      if (dragId != null) moveLead(dragId, s.key);
                      setDragId(null);
                    }}
                  >
                    <div className="crm-col-head" style={{ color: s.color }}>
                      <span>{s.icon} {s.label}</span>
                      <span className="crm-col-count">{cards.length}</span>
                    </div>
                    {formatMoney(quoted) && (
                      <div className="crm-col-total">{formatMoney(quoted)} quoted</div>
                    )}
                    {cards.map((lead) => {
                      const due = dueLabel(lead.next_follow_up);
                      return (
                        <div
                          key={lead.id}
                          className={`crm-lead-card ${dragId === lead.id ? "dragging" : ""}`}
                          style={{ borderLeftColor: s.color }}
                          draggable
                          onDragStart={() => setDragId(lead.id)}
                          onDragEnd={() => setDragId(null)}
                          onClick={() => setOpenLeadId(lead.id)}
                        >
                          <div className="crm-lead-name">{lead.client_name}</div>
                          <div className="crm-lead-meta">
                            {lead.event_type && <span>📸 {lead.event_type}</span>}
                            {formatDate(lead.event_date) && <span>📅 {formatDate(lead.event_date)}</span>}
                            {lead.client_phone && <span>📱 {lead.client_phone}</span>}
                          </div>
                          <div className="crm-lead-foot">
                            <span className="crm-lead-amount">{formatMoney(lead.quoted_amount) || ""}</span>
                            {due && lead.stage !== "booked" && lead.stage !== "lost" && (
                              <span className={`crm-due ${due.tone}`}>⏰ {due.text}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {cards.length === 0 && (
                      <div style={{ textAlign: "center", color: "#aaa", fontSize: 12.5, padding: "14px 0" }}>
                        Nothing here
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          </>
          )}
        </div>
      </div>

      {showModal && (
        <div className="crm-modal-backdrop" onClick={closeModal}>
          <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>＋ New Lead</h3>
            {formError && <div className="crm-form-error">{formError}</div>}
            <form onSubmit={createLead}>
              <div className="crm-form-grid">
                <div className="crm-field">
                  <label>Client name *</label>
                  <input value={form.client_name} onChange={set("client_name")} autoFocus placeholder="e.g. Priya & Arjun" />
                </div>
                <div className="crm-field">
                  <label>Phone</label>
                  <input value={form.client_phone} onChange={set("client_phone")} placeholder="+91 …" />
                </div>
                <div className="crm-field">
                  <label>Email</label>
                  <input type="email" value={form.client_email} onChange={set("client_email")} />
                </div>
                <div className="crm-field">
                  <label>City</label>
                  <input value={form.client_city} onChange={set("client_city")} />
                </div>
                <div className="crm-field">
                  <label>Event type</label>
                  <input value={form.event_type} onChange={set("event_type")} placeholder="Wedding, Birthday…" />
                </div>
                <div className="crm-field">
                  <label>Event date</label>
                  <input type="date" value={form.event_date} onChange={set("event_date")} />
                </div>
                <div className="crm-field">
                  <label>Venue</label>
                  <input value={form.venue} onChange={set("venue")} />
                </div>
                <div className="crm-field">
                  <label>How did they find you?</label>
                  <select value={form.source} onChange={set("source")}>
                    <option value="">— Select —</option>
                    {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="crm-field">
                  <label>Client budget</label>
                  <input type="number" min="0" value={form.budget} onChange={set("budget")} />
                </div>
                <div className="crm-field">
                  <label>Next follow-up</label>
                  <input type="date" value={form.next_follow_up} onChange={set("next_follow_up")} />
                </div>
                <div className="crm-field full">
                  <label>Notes</label>
                  <textarea value={form.notes} onChange={set("notes")} placeholder="Anything worth remembering about this enquiry" />
                </div>
              </div>
              <div className="crm-modal-actions">
                <button type="button" className="crm-btn ghost" onClick={closeModal}>Cancel</button>
                <button type="submit" className="crm-btn primary" disabled={saving}>
                  {saving ? "Adding…" : "Add lead"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {openLead && (
        <LeadDrawer
          lead={openLead}
          onClose={() => setOpenLeadId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

export default CrmLeads;
